import { useEffect } from "react";
import { Flex, Text } from "flicket-ui";
import { Select, Badge } from "~components";
import { FeeType, TicketAvailabilityStatus } from "~graphql/sdk";
import { ListItemDetails } from "../actions/item/actions.item.ListItemDetails";
import PriceWithFee from "../common/common.PriceWithFee";
import { getQuantityOptions } from "~lib/helpers/getQuantityOptions";
import { useRouter } from "next/router";
import {
  IReleaseTicket,
  TicketTypeType,
} from "~features/nonseated-reservation/nonseated-reservation.types";

type ListItemProps = {
  ticketType: IReleaseTicket;
  handleChange: (id: string, quantity: number, type: TicketTypeType) => void;
  quantity?: number;
  showHiddenFees?: boolean;
};

export const ListItem = ({
  handleChange,
  ticketType,
  showHiddenFees = false,
  quantity = undefined,
}: ListItemProps) => {
  const query = useRouter().query;
  const onChange = (value: number) =>
    handleChange(
      ticketType.id,
      value,
      ticketType.type === "addon" ? "addon" : "ticket"
    );

  const id = ticketType.id;

  useEffect(() => {
    if (query.ticket === id && query.quantity) {
      const id = setTimeout(() => {
        onChange(parseFloat(query.quantity as string));
      }, 2000);
      return () => {
        clearTimeout(id);
      };
    }
  }, []);

  const price = ticketType.price;

  const showHighDemandBadge =
    ticketType.inHighDemand &&
    ticketType.availabilityStatus === TicketAvailabilityStatus.Available;

  return (
    <Flex alignItems="center" justifyContent="space-between" mb={3} id={id}>
      <Flex flexDir="column" mr={1} w="100%">
        <Text
          fontSize={3}
          lineHeight="normal"
          fontWeight="extraBold"
          color="N800"
        >
          {ticketType.name}
        </Text>
        <Text
          fontSize={3}
          fontWeight="medium"
          lineHeight="normal"
          color="N600"
          mt="1/4"
        >
          <PriceWithFee
            price={price}
            showFees={showHiddenFees}
            isSoldOut={ticketType.isSoldOut}
            fee={ticketType.ticketFee}
            isPercentage={ticketType.ticketFeeType === FeeType.Percentage}
            stadiumLevyFee={ticketType.stadiumLevyFee}
          />
        </Text>
        {ticketType.availabilityStatus ===
          TicketAvailabilityStatus.AllocationExhausted && (
          <Text
            fontSize={2}
            fontWeight="medium"
            lineHeight="normal"
            color="N600"
            mt="1/2"
          >
            All tickets are currently in carts. Please try again later.
          </Text>
        )}
        <ListItemDetails
          name={ticketType.name}
          description={ticketType.description}
          restrictions={ticketType.restrictions}
          ticketImage={ticketType.image}
          ticketType={ticketType}
        />
      </Flex>
      <Flex alignItems="center">
        {ticketType.availabilityStatus ===
        TicketAvailabilityStatus.AllocationExhausted ? (
          <Text
            fontSize={1}
            lineHeight="normal"
            color="N600"
            minW="98px"
            textAlign="center"
          >
            Allocation exhausted
          </Text>
        ) : ticketType.availabilityStatus ===
            TicketAvailabilityStatus.Soldout || ticketType.isSoldOut ? (
          <Text
            fontSize={1}
            lineHeight="normal"
            color="N600"
            minW="98px"
            textAlign="center"
          >
            Sold out
          </Text>
        ) : (
          <>
            {showHighDemandBadge && <Badge>In High demand</Badge>}
            <Text fontSize={1} lineHeight="normal" color="N600" mr={1}>
              Quantity
            </Text>
            <Select
              minW="98px"
              options={getQuantityOptions(
                ticketType.minPurchaseQuantity,
                ticketType.maxPurchaseQuantity
              )}
              value={quantity}
              onChange={onChange}
              small
              useSingleValue
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
